/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */



import {usePrimeCache} from "./ServiceHooks";
import {service as SurveyQuestionDataTypeService} from "../Services/SurveyQuestionDataTypeService";
import {ObjectUtils} from "../Utils";

export const buildAnswer = (question, conservationAction, answer, overrides = {}) => ({
	// surveyQuestionId is sent as a fallback for the backend's code→id resolution;
	// code is the question identity — the backend always re-derives surveyQuestionId from it on save.
	surveyQuestionId: question?.surveyQuestionId,
	conservationActionId: conservationAction?.conservationActionId,
	...answer,
	// MUST stay below `...answer` (load-bearing ordering, not a mere fallback): a legacy answer can
	// carry code:null (pre-migration-287), and the spread above would clobber the resolved code with
	// it. Re-deriving here — answer's own code, else the question's authoritative code — after the
	// spread guarantees a non-null code wins. Moving this above `...answer` silently reintroduces the bug.
	code: answer?.code ?? question?.code,
	...overrides,
});

export const useSurveyFormInput = (props) => {
	const {
		ConservationAction: conservationAction,
		Question: question,
		Answer: answer = {},
		OnChange: parentOnChange,
		OnValidation: onValidation,
		Validation: validation,
	} = props;

	usePrimeCache([SurveyQuestionDataTypeService]);

	// this method is only used by SurveyFormInputBoolean and can be
	// replaced with SurveyQuestionDataTypeService calls once we
	// refactor SurveyFormInputBoolean to a functional component
	const getOptions = () => {
		const SurveyQuestionDataType = (SurveyQuestionDataTypeService.CachedValueById(question.surveyQuestionDataTypeId)?.result);

		if (!SurveyQuestionDataType) {
			return null
		}

		if (SurveyQuestionDataType.options) {
			const options = SurveyQuestionDataType?.options ?? {};
			return ObjectUtils.IsString(options) ? JSON.parse(options) : options;
		}
	}

	const onChange = (updateLogic) => {
		if (parentOnChange) {
			parentOnChange(updateLogic(buildAnswer(question, conservationAction, answer)));
		}
	}

	const setValidationStatusError = (message) => {
		if (onValidation) {
			onValidation(question.surveyQuestionId, message);
		}
	}

	return {
		setValidationStatusError: (message) => setValidationStatusError(message),
		markValidationStatusValid: () => setValidationStatusError(null),
		getOptions: getOptions,
		getAnswer: () => buildAnswer(question, conservationAction, answer),
		onChange,
		validationMessage: (validation != null ?
			<p className="sb-survey-validation-error">{validation}</p> :
			null)
	}
}
